import React, { useState, useEffect, useCallback } from 'react';
import {
  getScheduledTasks,
  pauseScheduledTask,
  resumeScheduledTask,
  deleteScheduledTask,
  ScheduledTask,
  formatSchedule,
} from '../../services/scheduledTasksApi';
import { formatRelativeTime } from '../../utils/dateFormatters';
import CreateScheduledTaskModal from './CreateScheduledTaskModal';
import EditScheduleModal from './EditScheduleModal';
import TaskExecutionHistory from './TaskExecutionHistory';
import { getSkillDisplay } from './skillDisplayMeta';
import { Button, StatusPill } from '../ui';
import type { StatusVariant } from '../ui';

interface ScheduledTasksPageProps {
  onClose: () => void;
  showSuccess?: (notification: any) => void;
  showError?: (notification: any) => void;
}

const ScheduledTasksPage: React.FC<ScheduledTasksPageProps> = ({
  onClose,
  showSuccess,
  showError,
}) => {
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<ScheduledTask | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedTasks = await getScheduledTasks();
      setTasks(fetchedTasks);
    } catch (err: any) {
      setError(err.message || 'Failed to load scheduled tasks');
      showError?.({ title: 'Error', message: err.message || 'Failed to load scheduled tasks' });
    } finally {
      setLoading(false);
    }
  }, [showError]);

  // Fetch tasks on mount
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handlePause = async (taskId: string) => {
    setActionLoading(taskId);
    try {
      await pauseScheduledTask(taskId);
      await fetchTasks();
      showSuccess?.({ title: 'Task Paused', message: 'Scheduled task has been paused' });
    } catch (err: any) {
      showError?.({ title: 'Error', message: err.message || 'Failed to pause task' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (taskId: string) => {
    setActionLoading(taskId);
    try {
      await resumeScheduledTask(taskId);
      await fetchTasks();
      showSuccess?.({ title: 'Task Resumed', message: 'Scheduled task has been resumed' });
    } catch (err: any) {
      showError?.({ title: 'Error', message: err.message || 'Failed to resume task' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this scheduled task?')) {
      return;
    }
    setActionLoading(taskId);
    try {
      await deleteScheduledTask(taskId);
      await fetchTasks();
      showSuccess?.({ title: 'Task Deleted', message: 'Scheduled task has been deleted' });
    } catch (err: any) {
      showError?.({ title: 'Error', message: err.message || 'Failed to delete task' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    fetchTasks();
    showSuccess?.({ title: 'Task Created', message: 'Scheduled task has been created successfully' });
  };

  const handleEditScheduleSuccess = () => {
    setEditingTask(null);
    fetchTasks();
    showSuccess?.({ title: 'Schedule Updated', message: 'Task schedule has been updated' });
  };

  const taskStatusVariant = (status: string): StatusVariant => {
    switch (status) {
      case 'active':    return 'live';
      case 'paused':    return 'paused';
      case 'completed': return 'approved';
      default:          return 'draft';
    }
  };

  const getLastRunStatusColor = (status: string | undefined): string => {
    if (!status) return 'var(--fg-3)';
    return status === 'success' ? 'var(--success)' : 'var(--danger)';
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'transparent',
    },
    header: {
      padding: '1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.5rem',
    },
    title: {
      fontSize: '1rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.5rem',
    },
    createButton: {
      padding: '0.5rem 0.875rem',
      backgroundColor: 'var(--accent)',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.8125rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      whiteSpace: 'nowrap',
    },
    closeButton: {
      padding: '0.5rem 0.75rem',
      backgroundColor: 'transparent',
      color: 'var(--fg-2)',
      border: '1px solid var(--border-1)',
      borderRadius: '6px',
      fontSize: '0.8125rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: '0 1rem 1rem',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      textAlign: 'center',
      color: 'var(--fg-2)',
    },
    taskList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    taskCard: {
      backgroundColor: 'var(--bg-surface-1)',
      borderRadius: '8px',
      padding: '0.875rem',
      border: '1px solid var(--border-1)',
      boxShadow: 'var(--shadow-xs)',
    },
    taskHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.625rem',
      gap: '0.5rem',
    },
    taskTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    statusBadge: {
      padding: '0.1875rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.6875rem',
      fontWeight: 500,
      textTransform: 'capitalize' as const,
      flexShrink: 0,
    },
    taskDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.375rem',
      marginBottom: '0.625rem',
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.5rem',
    },
    detailItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.125rem',
    },
    detailLabel: {
      fontSize: '0.6875rem',
      color: 'var(--fg-2)',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.025em',
    },
    detailValue: {
      fontSize: '0.8125rem',
      color: 'var(--fg-1)',
    },
    detailValueSmall: {
      fontSize: '0.75rem',
      color: 'var(--fg-2)',
    },
    taskPrompt: {
      fontSize: '0.75rem',
      color: 'var(--fg-2)',
      backgroundColor: 'var(--bg-surface-1)',
      padding: '0.5rem 0.625rem',
      borderRadius: '4px',
      marginBottom: '0.625rem',
      maxHeight: '3rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: 1.4,
    },
    taskActions: {
      display: 'flex',
      gap: '0.375rem',
      flexWrap: 'wrap',
    },
    actionButton: {
      padding: '0.3125rem 0.625rem',
      fontSize: '0.6875rem',
      fontWeight: 500,
      borderRadius: '4px',
      cursor: 'pointer',
      border: 'none',
      transition: 'opacity 0.2s, transform 0.1s',
    },
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    loadingText: {
      color: 'white',
      fontSize: '1rem',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Scheduled Tasks</h2>
        </div>
        <div style={{ ...styles.content, ...styles.emptyState }}>
          <p>Loading scheduled tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Scheduled Tasks</h2>
        <div style={styles.headerActions}>
          <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
            + New task
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      <div style={styles.content}>
        {error && (
          <div style={{ color: 'var(--danger)', marginBottom: '1rem', textAlign: 'center' }}>
            {error}
          </div>
        )}

        {tasks.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={{ marginBottom: '1rem' }}>No scheduled tasks yet.</p>
            <p style={{ fontSize: '0.875rem' }}>
              Create a scheduled task to automatically run skills at specified times.
            </p>
            <Button
              variant="primary"
              size="md"
              onClick={() => setShowCreateModal(true)}
              style={{ marginTop: '1rem' }}
            >
              Create your first task
            </Button>
          </div>
        ) : (
          <div style={styles.taskList}>
            {tasks.map((task) => (
              <div key={task.id} style={styles.taskCard}>
                {task.customSubject && (
                  <div style={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'var(--accent)',
                    marginBottom: '0.375rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {task.customSubject}
                  </div>
                )}
                <div style={styles.taskHeader}>
                  <h3 style={styles.taskTitle}>{getSkillDisplay(task.skillName).displayName}</h3>
                  <StatusPill variant={taskStatusVariant(task.status)} size="sm">
                    {task.status}
                  </StatusPill>
                </div>

                <div style={styles.taskDetails}>
                  {/* Schedule Row */}
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Schedule</span>
                    <span style={styles.detailValue}>{formatSchedule(task.schedule)}</span>
                  </div>

                  {/* Next/Last Run Row */}
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Next</span>
                    <span style={styles.detailValueSmall}>{formatRelativeTime(task.nextRunAt)}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Last</span>
                    <span style={styles.detailValueSmall}>
                      {task.lastRunAt ? (
                        <>
                          {formatRelativeTime(task.lastRunAt)}
                          <span
                            style={{
                              marginLeft: '0.25rem',
                              color: getLastRunStatusColor(task.lastRunStatus),
                              fontWeight: 500,
                            }}
                          >
                            • {task.lastRunStatus}
                          </span>
                        </>
                      ) : (
                        'Never'
                      )}
                    </span>
                  </div>

                  {/* Stats Row */}
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Runs</span>
                    <span style={styles.detailValueSmall}>
                      {task.runCount}
                      {task.failCount > 0 && (
                        <span style={{ color: 'var(--danger)', marginLeft: '0.25rem' }}>
                          ({task.failCount} failed)
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                <div style={styles.taskPrompt} title={task.prompt || undefined}>
                  {task.prompt && task.prompt.trim()
                    ? task.prompt
                    : getSkillDisplay(task.skillName).shortDescription}
                </div>

                <div style={styles.taskActions}>
                  {task.status === 'active' ? (
                    <Button
                      variant="spark"
                      size="sm"
                      onClick={() => handlePause(task.id)}
                      disabled={actionLoading === task.id}
                    >
                      {actionLoading === task.id ? '…' : 'Pause'}
                    </Button>
                  ) : task.status === 'paused' ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleResume(task.id)}
                      disabled={actionLoading === task.id}
                    >
                      {actionLoading === task.id ? '…' : 'Resume'}
                    </Button>
                  ) : null}

                  <Button
                    variant="partner"
                    size="sm"
                    onClick={() => setEditingTask(task)}
                    disabled={actionLoading === task.id}
                  >
                    Edit
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTaskId(task.id)}
                  >
                    History
                  </Button>

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(task.id)}
                    disabled={actionLoading === task.id}
                  >
                    {actionLoading === task.id ? '…' : 'Delete'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateScheduledTaskModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          showError={showError}
        />
      )}

      {editingTask && (
        <EditScheduleModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSuccess={handleEditScheduleSuccess}
          showError={showError}
        />
      )}

      {selectedTaskId && (
        <TaskExecutionHistory
          taskId={selectedTaskId}
          onClose={() => setSelectedTaskId(null)}
        />
      )}
    </div>
  );
};

export default ScheduledTasksPage;
